/* eslint-disable no-plusplus */
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import {
    AvailableGym,
    OptimizelyFeatureFlags,
    Product,
    ProductVariant,
    TggMembershipType,
    daypassTabParameterValue,
    membershipTabParameterName,
    studentTabParameterValue,
} from '@tgg/common-types';
import { axiosInstance } from '@tgg/micro-services/axios';
import { dispatchEvent, EventKey, ProductViewEvent } from '@tgg/services';
import {
    getPinoProducts,
    hasPinoProduct,
    relativeUrlWithBasePath,
} from '@tgg/util';

import { useFlagsContext, usePromoCodeContext } from '../../contexts';
import { withVerticalSpacing } from '../../hocs/withVerticalSpacing/withVerticalSpacing';
import { DayPassSelection, useDayPassSelection } from '../DayPassSelection';
import { Icon } from '../Icon';
import { StyledHeading } from '../MembershipPanel/MembershipPanel.styled';
import { PinoProducts } from '../PinoProducts';
import { Tabs } from '../Tabs';
import { TabItem } from '../Tabs/Tabs.types';

import { DayPassesPanel } from './DayPassesPanel';
import { hardcoded12MPills } from './Membership.constants';
import {
    StyledHeadContainer,
    StyledHeadProductContainer,
    StyledHeadProductDescriptionTile,
    StyledHeadProductHeading,
    StyledHeadProductsContainer,
    StyledHeadProductsText,
    StyledHeadProductsTitle,
    StyledHeadProductText,
    StyledDescription,
    StyledHeaderSection,
    StyledWrapper,
} from './Membership.styled';
import { MembershipProperties } from './Membership.types';
import { StudentPanel } from './StudentPanel';
import { MonthlyPanels } from './panels';

const filterByIncludedExcludedGyms =
    (branchId: string) =>
    (entry: {
        excludedGyms?: AvailableGym[];
        includedGyms?: AvailableGym[];
    }) => {
        if (
            entry.excludedGyms &&
            entry.excludedGyms.some(gym => gym.branchId === branchId)
        ) {
            return false;
        }

        if (
            entry.includedGyms &&
            entry.includedGyms.length > 0 &&
            !entry.includedGyms.some(gym => gym.branchId === branchId)
        ) {
            return false; // exclude from gym if the branchId is not in includedGyms
        }

        return true;
    };

/**
 * Note: When removing the original Membership and renaming this, make sure to bring over the schema file
 * It's not included here as we don't want to affect the Amplience pipeline scripts
 */
function MembershipComponent({
    id,
    text = '',
    description = '',
    ctaHref = '',
    products,
    selectedProductId,
    onSelect,
    productsAvailableAddons,
    gymName = '',
    openingHours,
    isKickerOffersRibbonEnabled,
    offSale = false,
    branchId,
    ctaName,
    productHighlightPills,
    offPeakTimes,
    isRejoin = false,
    promoCodes = [],
    isUsedOnMemberArea = false,
}: MembershipProperties) {
    const router = useRouter();
    const {
        Monthly: monthlyProducts,
        Fixed: fixedProducts,
        Student: studentProducts,
        DayPass: dayPassProducts,
    } = products;
    const doIt = monthlyProducts[TggMembershipType.Do_It];
    const liveIt = monthlyProducts[TggMembershipType.Live_It];
    const liveItFixed = fixedProducts.find(
        product => product.membershipType === TggMembershipType.Live_It_Fixed,
    );
    const areAnyMonthly = doIt || liveIt;
    const uniqueId = 'membership-types';

    const {
        flags: {
            [OptimizelyFeatureFlags.MEMBERSHIP_TERMS_VISIBILITY]: {
                enabled: isVisibleTermsFlagEnabled,
                variationKey: visibleTermsValidationKey,
            },
            [OptimizelyFeatureFlags.SHORTEN_MBT_PRICE_DRAW_TEST]: {
                enabled: isShortenMBTPriceDrawFlagEnabled,
                variationKey: shortenMBTValidationKey,
            },
            [OptimizelyFeatureFlags.PAYPAL_PAY_IN_3_PRODUCT_OPTIONS]: {
                enabled: isPaypalPayIn3Enabled,
                variationKey: isPaypalPayIn3EnabledVariationKey,
            },
            [OptimizelyFeatureFlags.PAYPAL_PAY_IN_3_TABLE_IFRAME]: {
                enabled: isPaypalIframeEnabled,
                variationKey: isPaypalIframeEnabledVariationKey,
            },
            [OptimizelyFeatureFlags.PRODUCT_TABLE_FREE_FREEZE_NOTICE]: {
                enabled: isFreeFreezeNoticeEnabled,
                variationKey: isFreeFreezeNoticeEnabledVariationKey,
            },
            [OptimizelyFeatureFlags.GUEST_PASS_ENABLED]: {
                enabled: isGuestPassFlagEnabled,
                variationKey: isGuestPassFlagEnabledVariationKey,
            },
            [OptimizelyFeatureFlags.MULTIPLE_ADD_ONS_ENABLED]: {
                enabled: isMultipleAddonsEnabled,
            },
            [OptimizelyFeatureFlags.PINO_TABLE_MBT_OPTIMIZATION]: {
                enabled: isMTBCardsOptimizationEnabled,
                variationKey: MBTCardsOptimizationVariationKey,
            },
            [OptimizelyFeatureFlags.MBT_PRICE_DRAWER_OPTIMISATION]: {
                enabled: isMBTOptimizationEnabledInOptimizely,
                variationKey: isMBTOptimizationEnabledVariationKey,
            },
        },
    } = useFlagsContext();

    const isMBTOptimizationEnabled =
        isMBTOptimizationEnabledInOptimizely && !isUsedOnMemberArea;
    const isMBTOptimization =
        isMBTOptimizationEnabled &&
        isMBTOptimizationEnabledVariationKey !== 'control_mbt';

    const filteredPillsForGym = productHighlightPills?.filter(
        filterByIncludedExcludedGyms(branchId),
    );

    const pillsForGym = isMBTOptimization
        ? hardcoded12MPills
        : filteredPillsForGym;

    const promoCodesForGym = promoCodes?.filter(
        filterByIncludedExcludedGyms(branchId),
    );

    const { handleDayPassProductSelection, ...dayPassSelectionProperties } =
        useDayPassSelection();

    // Open date picker if changeDate is present in the url params
    useEffect(() => {
        if (router.query.changeDate) {
            const currentProduct = products[ProductVariant.DayPass]?.find(
                p => p.id === selectedProductId,
            );

            if (currentProduct) {
                handleDayPassProductSelection(currentProduct);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let finalMonthlyProducts = [];

    if (areAnyMonthly) {
        const monthlyProductData = Object.values(monthlyProducts as Product);
        finalMonthlyProducts = monthlyProductData;
    }

    const legacyFixedProducts = fixedProducts.filter(
        product => product.classification === 'Legacy',
    );

    if (legacyFixedProducts.length > 0) {
        finalMonthlyProducts = [
            ...finalMonthlyProducts,
            ...legacyFixedProducts,
        ];
    }

    const isPinoProduct = hasPinoProduct(monthlyProducts);

    const membershipPinoProducts = getPinoProducts(products);

    let index = isPinoProduct || areAnyMonthly ? 1 : 0; // if there is a Pino/Monthly tab then the index is 1
    const studentTabIndex = studentProducts.length > 0 ? index++ : null;
    const dayPassTabIndex = dayPassProducts.length > 0 ? index : null;

    const [openTabIndex, setOpenTabIndex] = useState<number>(0);
    const [isPinoTabSelected, setIsPinoTabSelected] = useState<boolean>(false);

    const defineIsPinoTabSelected = (tabIndex: number) => {
        setIsPinoTabSelected(
            tabIndex !== studentTabIndex && tabIndex !== dayPassTabIndex,
        );
    };

    const defineOpenedTab = (preselectedMembership: ProductVariant) => {
        let tabIndex: number | null = null;

        /* istanbul ignore else - covered by tests */
        if (
            preselectedMembership &&
            preselectedMembership === ProductVariant.Student &&
            studentTabIndex !== null
        ) {
            tabIndex = studentTabIndex;
        } else if (
            preselectedMembership &&
            preselectedMembership === ProductVariant.DayPass &&
            dayPassTabIndex !== null
        ) {
            tabIndex = dayPassTabIndex;
        } else if (preselectedMembership) {
            tabIndex = 0; // default to first tab
        }

        /* istanbul ignore else */
        if (tabIndex !== null) {
            setOpenTabIndex(tabIndex);
            sendProductDetailEvent(tabIndex);
            defineIsPinoTabSelected(tabIndex);
        }
    };

    const { code: promoCodeFromContext } = usePromoCodeContext();

    const sendProductDetailEvent = (tabIndex: number) => {
        let selectedProductOptions;

        /* istanbul ignore else */
        if (tabIndex === studentTabIndex) {
            selectedProductOptions = studentProducts;
        } else if (tabIndex === dayPassTabIndex) {
            selectedProductOptions = dayPassProducts;
        } else if (isPinoProduct) {
            selectedProductOptions = membershipPinoProducts.Monthly;
        } else if (areAnyMonthly) {
            selectedProductOptions = finalMonthlyProducts;
        }

        const listOfProducts = selectedProductOptions?.map(product => {
            const {
                description: productName,
                expectedPaymentFrequency,
                price,
            } = product;
            return {
                name: productName,
                price: price.value,
                brand: gymName,
                category: expectedPaymentFrequency,
                variant: 'Create',
                quantity: 1,
            };
        });

        /* istanbul ignore else */
        if (listOfProducts) {
            void dispatchEvent<ProductViewEvent>(EventKey.PRODUCT_DETAIL, {
                event: EventKey.PRODUCT_DETAIL,
                ecommerce: {
                    detail: {
                        products: listOfProducts,
                    },
                },
            });
        }
    };

    const handleTabChange = (tabIndex: number) => {
        sendProductDetailEvent(tabIndex);
        defineIsPinoTabSelected(tabIndex);
    };

    // set preselected membership tab
    useEffect(() => {
        // get from url query
        const [path, queryAndHash] = router.asPath.split('?');
        const [queryString, hash] = (queryAndHash || '').split('#');

        // eslint-disable-next-line compat/compat
        const queries = new URLSearchParams(queryString);
        const membershipTab = queries.get(membershipTabParameterName);

        /* istanbul ignore else - covered by tests */
        if (membershipTab && membershipTab === studentTabParameterValue) {
            defineOpenedTab(ProductVariant.Student);
        } else if (
            membershipTab &&
            membershipTab === daypassTabParameterValue
        ) {
            defineOpenedTab(ProductVariant.DayPass);
        } else if (membershipTab) {
            defineOpenedTab(ProductVariant.None);
        }

        const wrapperFunction = async (branch: string, code: string) => {
            const membershipVariant = await getMembershipVariantByPromoCode(
                branch,
                code,
            );

            defineOpenedTab(membershipVariant);
        };
        // get from promoCodeContext
        /* istanbul ignore else */
        if (promoCodeFromContext) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            wrapperFunction(branchId, promoCodeFromContext);
        }

        if (!membershipTab && !promoCodeFromContext) {
            sendProductDetailEvent(0);
            defineIsPinoTabSelected(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady]);

    if (offSale) return null;

    const areTermsVisible =
        (isVisibleTermsFlagEnabled && visibleTermsValidationKey === 'V1') ||
        (isShortenMBTPriceDrawFlagEnabled &&
            shortenMBTValidationKey === 'v1_stdmbt_opd') ||
        (isShortenMBTPriceDrawFlagEnabled &&
            shortenMBTValidationKey === 'v3_shortmbt_opd');
    const finalTermsVisibility = isRejoin ? false : areTermsVisible;
    const isBenefitsShortened =
        (isShortenMBTPriceDrawFlagEnabled &&
            shortenMBTValidationKey === 'v2_shortmbt_cpd') ||
        (isShortenMBTPriceDrawFlagEnabled &&
            shortenMBTValidationKey === 'v3_shortmbt_opd');
    const isPaypalIframeVisible =
        isPaypalIframeEnabled && isPaypalIframeEnabledVariationKey === 'on';
    const isPaypalPayIn3Visible =
        isPaypalPayIn3Enabled && isPaypalPayIn3EnabledVariationKey === 'on';
    const isGuestPassEnabled =
        isGuestPassFlagEnabled && isGuestPassFlagEnabledVariationKey === 'on';
    const isFreeFreezeNoticeVisible =
        isFreeFreezeNoticeEnabled &&
        isFreeFreezeNoticeEnabledVariationKey === 'on';
    const isMBTCardsOptimizationEnabled =
        (isMTBCardsOptimizationEnabled &&
            MBTCardsOptimizationVariationKey === 'v1') ||
        isMBTOptimization;
    const isMBTOptimizationV1 =
        isMBTOptimizationEnabled &&
        isMBTOptimizationEnabledVariationKey === 'v1_original_plus';
    const isMBTOptimizationV2 =
        isMBTOptimizationEnabled &&
        isMBTOptimizationEnabledVariationKey === 'v2_wildcard_4_column';
    const isMBTOptimizationV3 =
        isMBTOptimizationEnabled &&
        isMBTOptimizationEnabledVariationKey === 'v3_wildcard_3_column';
    const isMBTOptimizationV2orV3 = isMBTOptimizationV2 || isMBTOptimizationV3;

    return (
        <>
            <StyledWrapper
                id={id ?? 'membership-table'}
                $isMBTOptimizationV2orV3={isMBTOptimizationV2orV3}
            >
                <StyledHeaderSection>
                    {text && !isPinoProduct ? (
                        <StyledHeading variant="h2">{text}</StyledHeading>
                    ) : null}
                    {description && (
                        <StyledDescription variant="body1">
                            {description}
                        </StyledDescription>
                    )}
                </StyledHeaderSection>
                {isMBTOptimization && (
                    <StyledHeadContainer>
                        <StyledHeadProductsTitle
                            variant="h2"
                            gutterBottom={false}
                        >
                            our memberships
                        </StyledHeadProductsTitle>
                        <StyledHeadProductsText gutterBottom={false}>
                            Our memberships are flexible—with convenient ways to
                            pay.
                        </StyledHeadProductsText>
                        <StyledHeadProductsContainer>
                            <StyledHeadProductContainer>
                                <StyledHeadProductDescriptionTile>
                                    <Icon name="cost" />
                                    <StyledHeadProductHeading>
                                        Saver Plans
                                    </StyledHeadProductHeading>
                                </StyledHeadProductDescriptionTile>
                                <StyledHeadProductText gutterBottom={false}>
                                    Save with our 9 or 12 month plans + no
                                    joining fees! Pay upfront or in 3
                                    instalments.
                                </StyledHeadProductText>
                            </StyledHeadProductContainer>
                            <StyledHeadProductContainer>
                                <StyledHeadProductDescriptionTile>
                                    <Icon name="payMonthly" />
                                    <StyledHeadProductHeading>
                                        Pay Monthly
                                    </StyledHeadProductHeading>
                                </StyledHeadProductDescriptionTile>
                                <StyledHeadProductText gutterBottom={false}>
                                    Pay month-to-month with no long-term
                                    commitment. Joining fees apply.
                                </StyledHeadProductText>
                            </StyledHeadProductContainer>
                        </StyledHeadProductsContainer>
                    </StyledHeadContainer>
                )}
                <Tabs
                    gymName={gymName}
                    items={
                        [
                            isPinoProduct
                                ? {
                                      label: 'memberships',
                                      content: (
                                          <PinoProducts
                                              products={membershipPinoProducts}
                                              gymName={gymName}
                                              isRejoin={isRejoin}
                                              offPeakTimes={offPeakTimes}
                                              ctaHref={ctaHref}
                                              productsAvailableAddons={
                                                  productsAvailableAddons
                                              }
                                              openingHours={openingHours}
                                              isPinoTabSelected={
                                                  isPinoTabSelected
                                              }
                                              onSelect={onSelect}
                                              isKickerOffersRibbonEnabled={
                                                  isKickerOffersRibbonEnabled
                                              }
                                              areTermsVisible={
                                                  finalTermsVisibility
                                              }
                                              productHighlightPills={
                                                  pillsForGym
                                              }
                                              isBenefitsShortened={
                                                  isBenefitsShortened
                                              }
                                              isGuestPassEnabled={
                                                  isGuestPassEnabled
                                              }
                                              ctaName={ctaName}
                                              isPaypalIframeVisible={
                                                  isPaypalIframeVisible
                                              }
                                              isPaypalPayIn3Visible={
                                                  isPaypalPayIn3Visible
                                              }
                                              isFreeFreezeNoticeVisible={
                                                  isFreeFreezeNoticeVisible
                                              }
                                              isMBTCardsOptimizationEnabled={
                                                  isMBTCardsOptimizationEnabled
                                              }
                                              isMultipleAddonsEnabled={
                                                  isMultipleAddonsEnabled
                                              }
                                              isMBTOptimization={
                                                  isMBTOptimization
                                              }
                                              promoCodes={promoCodesForGym}
                                              isMBTOptimizationV1={
                                                  isMBTOptimizationV1
                                              }
                                              isMBTOptimizationV2={
                                                  isMBTOptimizationV2
                                              }
                                              isMBTOptimizationV3={
                                                  isMBTOptimizationV3
                                              }
                                          />
                                      ),
                                      products: membershipPinoProducts.Monthly,
                                  }
                                : false,
                            areAnyMonthly && !isPinoProduct
                                ? {
                                      label: liveItFixed
                                          ? 'Memberships'
                                          : 'Monthly',
                                      content: (
                                          <MonthlyPanels
                                              liveIt={liveIt}
                                              doIt={doIt}
                                              liveItFixed={liveItFixed}
                                              gymName={gymName}
                                              ctaHref={ctaHref}
                                              onSelect={onSelect}
                                              productsAvailableAddons={
                                                  productsAvailableAddons
                                              }
                                              promoCodes={promoCodesForGym}
                                          />
                                      ),
                                      products: finalMonthlyProducts,
                                  }
                                : false,
                            studentProducts.length > 0
                                ? {
                                      label: 'Student',
                                      content: (
                                          <StudentPanel
                                              products={membershipPinoProducts}
                                              isRejoin={isRejoin}
                                              gymName={gymName}
                                              ctaHref={ctaHref}
                                              productsAvailableAddons={
                                                  productsAvailableAddons
                                              }
                                              openingHours={openingHours}
                                              onSelect={onSelect}
                                              areTermsVisible={
                                                  finalTermsVisibility
                                              }
                                              productHighlightPills={
                                                  pillsForGym
                                              }
                                              isBenefitsShortened={
                                                  isBenefitsShortened
                                              }
                                              ctaName={ctaName}
                                              isPaypalIframeVisible={
                                                  isPaypalIframeVisible
                                              }
                                              isPaypalPayIn3Visible={
                                                  isPaypalPayIn3Visible
                                              }
                                              isMBTCardsOptimizationEnabled={
                                                  isMBTCardsOptimizationEnabled
                                              }
                                              isMBTOptimization={
                                                  isMBTOptimization
                                              }
                                              promoCodes={promoCodesForGym}
                                              isMBTOptimizationV1={
                                                  isMBTOptimizationV1
                                              }
                                              isMBTOptimizationV2={
                                                  isMBTOptimizationV2
                                              }
                                              isMBTOptimizationV3={
                                                  isMBTOptimizationV3
                                              }
                                          />
                                      ),
                                      products: studentProducts,
                                  }
                                : false,
                            dayPassProducts.length > 0
                                ? {
                                      label: 'Day Passes',
                                      content: (
                                          <DayPassesPanel
                                              areAnyMonthly={!!areAnyMonthly}
                                              isRejoin={isRejoin}
                                              products={
                                                  membershipPinoProducts.DayPass
                                              }
                                              gymName={gymName}
                                              onSelect={
                                                  handleDayPassProductSelection
                                              }
                                              productHighlightPills={
                                                  pillsForGym
                                              }
                                              openingHours={openingHours}
                                              isMBTCardsOptimizationEnabled={
                                                  isMBTCardsOptimizationEnabled
                                              }
                                              isMBTOptimization={
                                                  isMBTOptimization
                                              }
                                              promoCodes={promoCodesForGym}
                                              isMBTOptimizationV1={
                                                  isMBTOptimizationV1
                                              }
                                              isMBTOptimizationV2={
                                                  isMBTOptimizationV2
                                              }
                                              isMBTOptimizationV3={
                                                  isMBTOptimizationV3
                                              }
                                              isMBTOptimizationV2orV3={
                                                  isMBTOptimizationV2orV3
                                              }
                                          />
                                      ),
                                      products: dayPassProducts,
                                  }
                                : false,
                        ].filter(Boolean) as TabItem[]
                    }
                    tabGroupAriaLabel="Membership Types"
                    uniqueId={uniqueId}
                    defaultSelectedIndex={openTabIndex}
                    onChange={handleTabChange}
                />
            </StyledWrapper>
            <DayPassSelection
                onSelect={onSelect}
                {...dayPassSelectionProperties}
            />
        </>
    );
}

export const Membership = withVerticalSpacing(MembershipComponent);

const getMembershipVariantByPromoCode = async (
    branchId: string,
    promoCode: string,
) => {
    const response = await axiosInstance.get<
        { branchId: string; promoCode: string },
        AxiosResponse<string>
    >(relativeUrlWithBasePath('/api/proxy/gyms/membershiptypes/'), {
        params: {
            branchId,
            promoCode,
        },
    });

    return ProductVariant[response.data as keyof typeof ProductVariant];
};
